import { HOAI_SERVICES, HOAI_SERVICE_NAMES, HOAI_SERVICE_PARTS, type NightBlocker } from '@pst/shared';
/**
 * German wording and formatting, in ONE place.
 *
 * Everything the interface says about dates, durations and relationships goes
 * through here, so that a table cell, a dialog and a tooltip cannot drift into
 * three different spellings of the same thing - which is exactly what they had
 * done before this file was made the only source.
 *
 * Conventions:
 * - Dates are always TT.MM.JJJJ, with a four-digit year. A two-digit year in a
 *   plan that spans a turn of the century is not a risk anyone should take,
 *   and mixing both forms on one screen looks like two different things.
 * - Units stand in the column heading, not in every cell: "Dauer (AT)" above
 *   plain numbers. Repeating "AT" in forty rows is noise.
 * - Relationship types use the terms of DIN 69900 (Netzplantechnik), the
 *   vocabulary of German construction scheduling, not English abbreviations.
 */

/** Arbeitstage - the one unit of duration in PST (decision A-11). */
export const UNIT_DAYS = 'AT';
export const UNIT_DAYS_LONG = 'Arbeitstage';

/**
 * TT.MM.JJJJ. Done by splitting the string, not through Date, which would
 * bring a time zone into a value that deliberately has none.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return iso;
  return `${day}.${month}.${year}`;
}

/** For instants such as "last calculated", in the viewer's local time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The local calendar day of an instant, TT.MM.JJJJ - e.g. a fetch at 01:00 belongs to that day, not to the UTC one before. */
export function formatDayOf(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** A signed number, as used for variance: +3, -2, 0. */
export function formatSigned(value: number): string {
  return value > 0 ? `+${value}` : `${value}`;
}

/**
 * Relationship types after DIN 69900. The internal codes stay the English
 * ones the engine and MSPDI use; only the interface speaks German.
 */
export const RELATIONSHIPS = {
  FS: { short: 'NF', name: 'Normalfolge', flow: 'Ende → Anfang' },
  SS: { short: 'AF', name: 'Anfangsfolge', flow: 'Anfang → Anfang' },
  FF: { short: 'EF', name: 'Endfolge', flow: 'Ende → Ende' },
  SF: { short: 'SF', name: 'Sprungfolge', flow: 'Anfang → Ende' },
} as const;

export type RelationshipCode = keyof typeof RELATIONSHIPS;

export function relationshipLabel(type: string): string {
  const entry = RELATIONSHIPS[type as RelationshipCode];
  return entry ? `${entry.name} (${entry.short}) – ${entry.flow}` : type;
}

export function relationshipShort(type: string): string {
  return RELATIONSHIPS[type as RelationshipCode]?.short ?? type;
}

/**
 * The predecessors of a task in the compact notation of the established
 * tools: the predecessor's number, then type and lag where they differ from
 * the plain case. A normal sequence without lag is just the number.
 *
 *   10          Normalfolge from 10, no lag
 *   10NF+5      Normalfolge from 10, five working days lag
 *   20AF        Anfangsfolge from 20
 *   30EF-2      Endfolge from 30, two days overlap
 */
export function predecessorNotation(
  links: readonly { code: string; type: string; lagDays: number }[],
): string {
  return links
    .map((link) => {
      const plain = link.type === 'FS' && link.lagDays === 0;
      if (plain) return link.code;
      const lag = link.lagDays === 0 ? '' : formatSigned(link.lagDays);
      return `${link.code}${relationshipShort(link.type)}${lag}`;
    })
    .join('; ');
}

/**
 * Status of a task, as the team reports it. Entered by hand; the calculation
 * does not look at it.
 */
export const TASK_STATUS = {
  open: { label: 'offen', color: 'gray' },
  running: { label: 'läuft', color: 'brand' },
  done: { label: 'erledigt', color: 'green' },
  blocked: { label: 'blockiert', color: 'red' },
} as const;
export type TaskStatusKey = keyof typeof TASK_STATUS;

/** German federal states for the holiday calendar, by their usual codes. */
export const BUNDESLAENDER: readonly { value: string; label: string }[] = [
  { value: 'BW', label: 'Baden-Württemberg' },
  { value: 'BY', label: 'Bayern' },
  { value: 'BE', label: 'Berlin' },
  { value: 'BB', label: 'Brandenburg' },
  { value: 'HB', label: 'Bremen' },
  { value: 'HH', label: 'Hamburg' },
  { value: 'HE', label: 'Hessen' },
  { value: 'MV', label: 'Mecklenburg-Vorpommern' },
  { value: 'NI', label: 'Niedersachsen' },
  { value: 'NW', label: 'Nordrhein-Westfalen' },
  { value: 'RP', label: 'Rheinland-Pfalz' },
  { value: 'SL', label: 'Saarland' },
  { value: 'SN', label: 'Sachsen' },
  { value: 'ST', label: 'Sachsen-Anhalt' },
  { value: 'SH', label: 'Schleswig-Holstein' },
  { value: 'TH', label: 'Thüringen' },
];

export const WEEKDAYS: readonly { value: number; short: string }[] = [
  { value: 1, short: 'Mo' },
  { value: 2, short: 'Di' },
  { value: 3, short: 'Mi' },
  { value: 4, short: 'Do' },
  { value: 5, short: 'Fr' },
  { value: 6, short: 'Sa' },
  { value: 7, short: 'So' },
];

/** Euro amounts, German style: 20.400,00 €. */
export function formatEuro(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

// --- Kostenplanung und Vergabe ---------------------------------------------

/** Cost stages after DIN 276, in their order through the project. */
export const COST_STAGES = [
  { key: 'frame', label: 'Kostenrahmen', short: 'Rahmen' },
  { key: 'estimate', label: 'Kostenschätzung', short: 'Schätzung' },
  { key: 'calculation', label: 'Kostenberechnung', short: 'Berechnung' },
  { key: 'quote', label: 'Kostenanschlag', short: 'Anschlag' },
  { key: 'final', label: 'Kostenfeststellung', short: 'Feststellung' },
] as const;
export type CostStageKey = (typeof COST_STAGES)[number]['key'];

/** Filters of the benchmark catalog (AP-87). */
export const BENCHMARK_FILTERS = {
  words: 'Suchen: Bezeichnung, KG, Gebäudeart …',
  buildingType: 'Gebäudeart',
  costGroup: 'KG beginnt mit',
  level: 'KG-Ebene',
  unit: 'Bezugseinheit',
  edition: 'Quelle, Ausgabe',
  region: 'Region',
  kinds: { Gebäude: 'Gebäude', Bauelement: 'Bauelemente', Position: 'Positionen', sonstige: 'sonstige' },
  levels: { 1: '1. Ebene', 2: '2. Ebene', 3: '3. Ebene', 4: 'darunter' },
} as const;

/** How deep the cost plan's tree is open (AP-86). */
export const COST_DEPTHS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: 'all', label: 'alle' },
];

/** Marks on an estimate line: how certain its figure is. */
export const COST_FLAGS = {
  A: { label: 'Annahme', short: 'A', color: 'gray', hint: 'angenommen, plausibel' },
  '?': { label: 'offen', short: '?', color: 'yellow', hint: 'offene Frage, zu klären' },
  '!': { label: 'Vorsicht', short: '!', color: 'red', hint: 'unsicher oder mit Risiko' },
} as const;

export const COST_VALUE_KINDS = {
  quantity: { label: 'Mengen', one: 'Menge', hint: 'Flächen, Längen, Stückzahlen' },
  rate: { label: 'Kennwerte', one: 'Kennwert', hint: '€ je Einheit, Pauschalen, Prozentsätze' },
  ratio: { label: 'Kennzahlen', one: 'Kennzahl', hint: 'aus den Summen: GESAMT (mit Risiko), NETTO (ohne), KG(300)' },
} as const;

export const PACKAGE_STATUS = {
  draft: { label: 'in Vorbereitung', color: 'gray' },
  sent: { label: 'versendet', color: 'brand' },
  offers: { label: 'Angebote liegen vor', color: 'teal' },
  awarded: { label: 'vergeben', color: 'green' },
  cancelled: { label: 'aufgehoben', color: 'red' },
} as const;
export type PackageStatusKey = keyof typeof PACKAGE_STATUS;

export const PROCEDURES = [
  { value: 'open', label: 'Öffentliche Ausschreibung' },
  { value: 'restricted', label: 'Beschränkte Ausschreibung' },
  { value: 'negotiated', label: 'Verhandlungsvergabe' },
  { value: 'direct', label: 'Direktauftrag / Angebotsanfrage' },
] as const;

export const POSITION_TYPES = [
  { value: 'normal', label: 'Normalposition', short: '' },
  { value: 'lump', label: 'Pauschal', short: 'psch' },
  { value: 'contingency', label: 'Bedarfsposition', short: 'Bedarf' },
  { value: 'alternative', label: 'Wahlposition', short: 'Wahl' },
] as const;

/** Destatis' signs next to or instead of a value, as in its key ("Zeichenerklärung"). */
export const DESTATIS_SIGNS: Record<string, string> = {
  '-': 'nichts vorhanden, genau Null oder zur statistischen Geheimhaltung auf Null geändert',
  e: 'endgültiger Wert',
  '0': 'Zahlenwert von Null verschieden, jedoch so nahe an Null, dass auf Null gerundet',
  r: 'berichtigte Zahl',
  p: 'vorläufige Zahl',
  s: 'geschätzte Zahl',
  '()': 'Aussagewert eingeschränkt, da der Zahlenwert statistisch relativ unsicher ist',
  '/': 'keine Angaben, da Zahlenwert nicht sicher genug',
  '.': 'Zahlenwert unbekannt oder geheim zu halten',
  '...': 'Angabe fällt später an',
  x: 'Tabellenfach gesperrt, weil Aussage nicht sinnvoll',
};
/** Signs that make a value unsure enough to mention where it is used. */
export const DESTATIS_UNSURE: Record<string, string> = { p: 'vorläufig', s: 'geschätzt', '()': 'Aussagewert eingeschränkt' };

/** Building types of the Destatis building price index (AP-80). */
export const PRICE_INDEX_TYPES = ['Wohngebäude', 'Bürogebäude', 'Gewerbliche Betriebsgebäude'] as const;

/** A quarter as shown: "2025-Q1" → "1. Quartal 2025". */
export function formatQuarter(period: string): string {
  const m = /^(\d{4})-Q([1-4])$/.exec(period);
  return m ? `${m[2]}. Quartal ${m[1]}` : /^\d{4}$/.test(period) ? `Jahr ${period}` : period;
}

/** Text libraries of the tendering module (AP-78): what each field of an entry means. */
export const LIBRARY_FIELDS = {
  shortText: { label: 'Kurztext' },
  longText: { label: 'Langtext' },
  unit: { label: 'Einheit' },
  positionType: { label: 'Art', hint: 'Normal-, Pauschal-, Bedarfs- (in Katalogen oft „Nur EP“) oder Wahlposition; geht bei der Übernahme mit ins LV' },
  source: { label: 'Quelle', hint: 'woher der Text stammt, z. B. „Herstellerdownload 2026“ oder „eigener Bürotext“' },
  attribution: { label: 'Herkunftsvermerk', hint: 'Rechte- oder Urhebervermerk, der mit dem Text in das LV übernommen wird' },
  origin: { label: 'Herkunft', hint: 'aus einer Textbibliothek übernommen; spätere Änderungen der Bibliothek wirken hier nicht' },
  oz: { label: 'OZ', hint: 'Ordnungszahl aus der eingelesenen Datei' },
  note: { label: 'Vorbemerkung', hint: 'gilt für die Positionen darunter; kommt als eigene Zeile ohne OZ ins Leistungsverzeichnis' },
  importLog: { label: 'Einleseprotokoll', hint: 'was beim Einlesen übernommen wurde und was nicht' },
} as const;

/** How an import turned out (AP-81), for the log and the import dialog. */
export const IMPORT_OUTCOMES = {
  created: { label: 'neu angelegt', color: 'green' },
  replaced: { label: 'ersetzt', color: 'brand' },
  appended: { label: 'angehängt', color: 'brand' },
  skipped: { label: 'übersprungen', color: 'gray' },
  failed: { label: 'fehlgeschlagen', color: 'red' },
  preview: { label: 'Vorschau', color: 'gray' },
} as const;
export const IMPORT_TRIGGERS = { manual: 'von Hand', inbox: 'Eingangsordner' } as const;
/** What the inbox did with a file (ADR 0009). */
export const INBOX_OUTCOMES = {
  ok: { label: 'verarbeitet', color: 'green' },
  skipped: { label: 'übersprungen', color: 'gray' },
  failed: { label: 'fehlgeschlagen', color: 'red' },
} as const;

/** The tables under Verwaltung › Bibliotheken (AP-128): row menu, detail popup, column grips. */
export const TABLE_TEXTS = {
  view: 'Ansehen',
  edit: 'Bearbeiten …',
  remove: 'Löschen …',
  actions: 'Aktionen',
  save: 'Speichern',
  cancel: 'Abbrechen',
  resize: 'Spaltenbreite',
  resizeHint: 'Ziehen oder Pfeiltasten (mit Umschalt in großen Schritten); Doppelklick: Standardbreite',
  badNumber: 'keine gültige Zahl',
  required: 'darf nicht leer sein',
  saveFailed: 'Speichern fehlgeschlagen.',
  hint: 'Klick wählt eine Zeile, Doppelklick oder Enter öffnet sie; Rechtsklick, ⋯ oder Umschalt + F10 für das Menü. Spaltenbreite am Kopf ziehen.',
} as const;

/**
 * Portals with tender texts to download by hand (Weg A, 2026-09-25): PST
 * only opens them in a new tab; the downloaded GAEB file is then dropped
 * into PST. Terms of use: docs/QUELLEN-AUSSCHREIBUNGSTEXTE.md.
 */
export const TEXT_PORTALS = [
  { label: 'ausschreiben.de', url: 'https://ausschreiben.de/', hint: 'Herstellertexte, ohne Anmeldung, Download als GAEB X81' },
  { label: 'heinze.de', url: 'https://www.heinze.de/tools/ausschreibungstexte/', hint: 'Herstellertexte, mit Konto, Download als GAEB XML' },
] as const;

/** Portals PST fetches from with the office's account (ADR 0023), in the order of the plan. */
export const FETCH_PORTALS = {
  ausschreiben: { label: 'ausschreiben.de', url: 'https://ausschreiben.de/', what: 'Herstellertexte als GAEB-Dateien in die Ausschreibungstexte' },
  baupreislexikon: { label: 'Baupreislexikon', url: 'https://www.baupreislexikon.de/', what: 'Preise mit Abrufdatum und Preisstand' },
  heinze: { label: 'heinze.de', url: 'https://www.heinze.de/', what: 'Herstellertexte als GAEB-Dateien in die Ausschreibungstexte' },
} as const;

/** What a fetch job takes as its target, per portal with an adapter (AP-122 ff.). */
export const PORTAL_JOB_TARGETS: Partial<Record<'ausschreiben' | 'baupreislexikon' | 'heinze', { label: string; placeholder: string; hint: string }>> = {
  ausschreiben: {
    label: 'Katalog oder Ordner',
    placeholder: 'https://ausschreiben.de/catalog/kaldewei',
    hint: 'Adresse eines Herstellerkatalogs oder eines Ordners darin (im Browser öffnen, Adresse kopieren). PST lädt die Positionstexte als GAEB XML ohne Bilder und liest sie in die Ausschreibungstexte ein.',
  },
  heinze: {
    label: 'Hersteller oder Ordner im AT-Manager',
    placeholder: 'https://www.heinze.de/at-manager/manager/firma/22050795',
    hint: 'Adresse eines Herstellers oder Ordners im Ausschreibungstext-Manager (heinze.de/at-manager, Hersteller wählen, Adresse kopieren). PST meldet sich an, lädt GAEB XML 3.2 (DA81, mit Gliederung) und liest es in die Ausschreibungstexte ein.',
  },
};

/** Kinds and states of a fetch job (ADR 0023). */
export const PORTAL_JOB_KINDS = {
  anmeldeprobe: 'Probeanmeldung',
  einzel: 'Einzelabfrage',
  haeppchen: 'Häppchen (nachts)',
  auftrag: 'Auftrag',
} as const;
export const PORTAL_JOB_STATES = {
  neu: { label: 'wartet', color: 'gray' },
  läuft: { label: 'läuft', color: 'blue' },
  fertig: { label: 'fertig', color: 'green' },
  gestoppt: { label: 'gestoppt (Warnzeichen)', color: 'red' },
  abgebrochen: { label: 'abgebrochen', color: 'gray' },
  fehler: { label: 'Fehler', color: 'orange' },
} as const;

/** The switch that allows fetching at all, and the one for the night round (PLAN-PORTALE.md 3.4b). */
export const PORTAL_ALLOW = {
  label: 'Abrufe mit diesem Konto erlauben',
  hint: 'Ohne diesen Haken ruft PST nichts ab: keine Aufträge von Hand, keine Einzelabfragen, kein Nachtlauf. Probeanmeldungen gehen immer.',
  on: 'Abrufe erlaubt',
  off: 'Abrufe aus',
} as const;
export const PORTAL_NIGHT = {
  label: 'Vollabzug nachts (Umlauf über alle Hersteller bzw. Seiten, älteste zuerst)',
  hint: 'Je Nacht ein Häppchen im Zeitfenster, innerhalb des Tageslimits; in der ersten Woche mit halbem Tageslimit, danach ein Bericht. Bei jedem Warnzeichen hält PST an und stoppt das Portal.',
  recommend: 'Für den Nachtumlauf empfohlene Drosselung',
  recommendApply: 'Empfohlene Werte ins Formular übernehmen',
} as const;

/** Why no chunk of the night round runs tonight (one reason, the first that applies). */
export const NIGHT_BLOCKERS = {
  aus: 'Vollabzug nachts ist aus',
  'kein-zugang': 'keine Zugangsdaten gespeichert',
  'nicht-erlaubt': 'Abrufe mit diesem Konto sind nicht erlaubt',
  gestoppt: 'Portal gestoppt – erst wieder freigeben',
  'keine-probe': 'noch keine gelungene Probeanmeldung',
  dienst: 'wartet auf den Abzugsdienst (Häppchen noch nicht gebaut)',
  angelegt: 'Häppchen dieser Nacht schon angelegt',
  tageslimit: 'Tageslimit für Häppchen erreicht (80 %)',
} as const satisfies Record<NightBlocker, string>;

/** "noch 46 Nächte (geschätzt)", from the nights so far. */
export function nightsLeft(n: number | null): string {
  if (n === null) return 'Restdauer noch nicht schätzbar';
  if (n === 0) return 'erster Umlauf vollständig';
  return `noch etwa ${n.toLocaleString('de-DE')} ${n === 1 ? 'Nacht' : 'Nächte'} (geschätzt)`;
}

/** The throttle of a portal fetch, for the settings form (bounds come from @pst/shared). */
export const PORTAL_THROTTLE_FIELDS = {
  dailyLimit: { label: 'Seitenaufrufe je Tag', hint: 'Tageslimit je Portal, Einzelabfragen und Probeanmeldungen eingeschlossen' },
  pauseMinSeconds: { label: 'Pause von (s)', hint: 'zufällige Pause zwischen zwei Seitenaufrufen, untere Grenze' },
  pauseMaxSeconds: { label: 'Pause bis (s)', hint: 'zufällige Pause zwischen zwei Seitenaufrufen, obere Grenze' },
  burstEvery: { label: 'Lange Pause nach … Aufrufen', hint: 'nach so vielen Seitenaufrufen eine längere Pause' },
  burstPauseMinutes: { label: 'Lange Pause (min)', hint: 'Dauer der längeren Pause' },
  downloadPauseSeconds: { label: 'Pause nach Download (s)', hint: 'Pause nach jeder heruntergeladenen Datei' },
  runMinutes: { label: 'Längster Lauf (min)', hint: 'ein nächtliches Häppchen endet spätestens nach dieser Zeit' },
} as const;

/** GAEB DA XML files PST reads into a text library (AP-79). */
export const GAEB_ACCEPT = '.x81,.x82,.x83,.x84,.x86,.xml';

/** The sentinel under a lazily loaded list. */
export function lazyMore(rest: number): string {
  return `weitere ${rest.toLocaleString('de-DE')} Zeilen laden …`;
}

/** Levels of the system log (Einstellungen › Systemprotokoll). */
export const LOG_LEVELS = {
  error: { label: 'Fehler', color: 'red' },
  warn: { label: 'Warnung', color: 'orange' },
  log: { label: 'Info', color: 'brand' },
  debug: { label: 'Debug', color: 'gray' },
} as const;

export const BIDDER_STATUS = {
  invited: { label: 'angefragt', color: 'gray' },
  submitted: { label: 'Angebot abgegeben', color: 'green' },
  declined: { label: 'abgesagt', color: 'orange' },
  excluded: { label: 'ausgeschlossen', color: 'red' },
} as const;
export type BidderStatusKey = keyof typeof BIDDER_STATUS;

/** 1.234,56 - without currency sign, for table cells whose heading carries "€". */
export function formatAmount(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Quantities: up to three decimals, no trailing zeros. */
/** Why a project snapshot was taken. */
export const SNAPSHOT_REASONS: Readonly<Record<string, string>> = {
  open: 'Projekt geöffnet',
  manual: 'von Hand',
  'before-restore': 'vor Wiederherstellung',
  'before-rollforward': 'vor Fortschreibung',
};

/** File sizes as "1,2 MB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toLocaleString('de-DE', { maximumFractionDigits: i === 0 ? 0 : 1 })} ${units[i]}`;
}

/** Hours as "2,5 h"; whole hours without decimals. */
export function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return `${value.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h`;
}

/**
 * Time recording: the fixed choice for hours that belong to no category, and
 * its value in a select (a select needs a string; the API takes null).
 */
export const UNCATEGORIZED = 'Ohne Kategorie';
export const UNCATEGORIZED_VALUE = 'ohne';

/** The label of a sum line under a table. */
export const TOTAL_LABEL = 'Gesamt';

/** Today in the browser's time zone, as YYYY-MM-DD. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}

export function formatPercent(value: number | null | undefined, signed = false): string {
  if (value === null || value === undefined) return '—';
  const text = `${Math.abs(value).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
  if (!signed) return text;
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text;
}

/**
 * A number as typed or pasted in German: "1.234,56", "58", "12,5", also
 * "1234.56" from an English spreadsheet. Empty gives null, nonsense undefined.
 */
export function parseGermanNumber(input: string): number | null | undefined {
  const s = input.trim().replace(/\s|€/g, '');
  if (s === '') return null;
  let normal: string;
  if (s.includes(',')) normal = s.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) normal = s.replace(/\./g, '');
  else normal = s;
  const n = Number(normal);
  return Number.isFinite(n) ? n : undefined;
}

// --- Angebote (Modul 5) ---------------------------------------------------------

/** States of a supplement (Nachtrag, AP-106). */
export const SUPPLEMENT_STATUS = {
  angemeldet: { label: 'angemeldet', color: 'gray' },
  angeboten: { label: 'angeboten', color: 'brand' },
  geprueft: { label: 'geprüft', color: 'yellow' },
  beauftragt: { label: 'beauftragt', color: 'green' },
  abgelehnt: { label: 'abgelehnt', color: 'red' },
} as const;

/** Kinds of entries in the DIN 277 library (AP-109). */
export const DIN277_KINDS = {
  begriff: { label: 'Begriff', color: 'brand' },
  gliederung: { label: 'Teilfläche', color: 'teal' },
  regel: { label: 'Regel', color: 'gray' },
} as const;

/** The HOAI services PST calculates (AP-104). */
export const HOAI_SERVICE_LABELS = HOAI_SERVICE_NAMES;

/** Leistungsbilder grouped by the HOAI's parts, for a select. */
export function hoaiServiceOptions(): { group: string; items: { value: string; label: string }[] }[] {
  const groups = new Map<string, { value: string; label: string }[]>();
  for (const k of HOAI_SERVICES) {
    const part = HOAI_SERVICE_PARTS[k];
    groups.set(part, [...(groups.get(part) ?? []), { value: k, label: HOAI_SERVICE_NAMES[k] }]);
  }
  return [...groups].map(([group, items]) => ({ group, items }));
}


/** What a project from an offer takes over (AP-98). */
export const PROJECT_FROM_OFFER_PARTS = {
  projectdata: { label: 'Projektdaten und Kontakte', hint: 'Beschreibung, Auftraggebende, Angebotssumme, Bindefrist, Annahmen, Risiken; Auftraggebende, Bietende, Nachunternehmen als Kontakte' },
  time: { label: 'Zeiterfassung', hint: 'je Position eine Kategorie mit den kalkulierten Stunden als Budget; Zusatzleistungen als nicht beauftragt' },
  schedule: { label: 'Terminplan', hint: 'je Position ein Vorgang, Dauer aus Stunden und Besetzung, als Basisplan festgehalten' },
  costs: { label: 'Kostenplanung', hint: 'Gliederung nach DIN 276 bis zur gewählten Ebene' },
} as const;

export const OFFER_STATUS = {
  entwurf: { label: 'Entwurf', color: 'gray' },
  abgegeben: { label: 'abgegeben', color: 'brand' },
  beauftragt: { label: 'beauftragt', color: 'green' },
  abgelehnt: { label: 'abgelehnt', color: 'red' },
  'zurückgezogen': { label: 'zurückgezogen', color: 'gray' },
} as const;

/** Why a snapshot of an offer was taken (ADR 0018). */
export const OFFER_SNAPSHOT_REASONS = {
  open: 'beim Öffnen',
  manual: 'von Hand',
  'before-restore': 'vor Wiederherstellen',
} as const;

/** The tabs of an offer, in this order (page and menus). */
export const OFFER_TABS = [
  { path: 'auswertung', label: 'Auswertung' },
  { path: 'angaben', label: 'Angaben und Texte' },
  { path: 'kalkulation', label: 'Kalkulation' },
  { path: 'vergleich', label: 'Vergleich der Varianten' },
  { path: 'hoai', label: 'HOAI' },
  { path: 'dateien', label: 'Dateien' },
] as const;

export const CONSTELLATION_LABELS = {
  einzel: 'Einzelbieter',
  einzel_nu: 'Einzelbieter mit Nachunternehmer',
  bg: 'Bietergemeinschaft',
  bg_nu: 'Bietergemeinschaft mit Nachunternehmer',
} as const;

/** Where the details of a new offer come from (AP-92, AP-96, AP-111). */
export const OFFER_SOURCES = {
  documents: { label: 'Aus Unterlagen', hint: 'Auslobung als PDF, ZIP, Word oder Excel – die KI wertet sie aus, Sie geben frei' },
  text: { label: 'Aus einer Beschreibung', hint: 'freier Text zur Aufgabe – die KI wertet ihn aus, Sie geben frei' },
  none: { label: 'Ohne Vorlage', hint: 'Angaben, Kalkulation und Texte selbst ausfüllen' },
} as const;

/** How the fee is found (AP-111). */
export const FEE_BASIS_LABELS = {
  hours: { label: 'Nach Aufwand', hint: 'Stunden je Rolle und Position, Stundensätze des Büros' },
  hoai: { label: 'Nach HOAI', hint: 'Honorar nach HOAI 2021 aus Bezugsgröße, Honorarzone und Leistungsphasen (Orientierungswerte)' },
  both: { label: 'Beides zum Vergleich', hint: 'Stundenkalkulation und HOAI nebeneinander' },
} as const;

/** The AI evaluation (AP-97): states of a run, models and effort (Einstellungen › KI-Auswertung). */
export const AI_RUN_STATES = {
  neu: { label: 'wartet auf den Server', color: 'gray' },
  läuft: { label: 'läuft', color: 'brand' },
  fertig: { label: 'fertig', color: 'green' },
  fehler: { label: 'fehlgeschlagen', color: 'red' },
} as const;
export const AI_STATIONS = { analyse: 'Auswertung', entwurf: 'Entwurf' } as const;
export const AI_MODELS = { fable: 'Fable (am gründlichsten)', opus: 'Opus', sonnet: 'Sonnet (schneller)' } as const;
export const AI_EFFORTS = { medium: 'mittel', high: 'hoch', xhigh: 'sehr hoch', max: 'höchster' } as const;
/** The parts of the evaluation, as the office's working rules list them. */
export const ANALYSIS_SECTIONS = {
  extras: 'Geforderte Extra- und Optionsleistungen',
  deadlines: 'Fristen und Termine',
  form: 'Formvorgaben',
  evidence: 'Geforderte Nachweise und Erklärungen',
  award: 'Wertung, Zuschlag, Budget',
  gaps: 'Widersprüche, Lücken, unfertige Textstellen',
  interfaces: 'Schnittstellen zu anderen Losen oder Leistungen',
  legal: 'Landesrechtliche Bezüge (zu prüfen, nicht bewertet)',
} as const;
/** Whether the documents provide for a fee after the HOAI (AP-111). */
export const HOAI_APPLIES = { ja: 'vorgesehen', nein: 'nicht vorgesehen', unklar: 'unklar' } as const;
export const ROMAN_ZONES: Readonly<Record<number, string>> = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V' };
export const AI_MARKS = { '': null, A: { label: '[A]', title: 'Annahme', color: 'yellow' }, '?': { label: '[?]', title: 'offen – die Unterlagen sagen es nicht', color: 'orange' } } as const;
export const formatSeconds = (s: number): string => (s < 90 ? `${Math.round(s)} s` : `${Math.round(s / 60)} min`);
/** Cost of a run over the Claude API, billed in US dollars: „0,84 US-$“. */
export const formatUsd = (value: number): string => `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} US-$`;
/** A whole count with thousands separator: „12.400“. */
export const formatCount = (value: number): string => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value);

export const PARTY_FIELDS = {
  name: 'Name / Firma',
  profession: 'Berufsbezeichnung',
  street: 'Straße Hausnummer',
  city: 'PLZ Ort',
  phone: 'Telefon',
  email: 'E-Mail',
  chamber: 'Kammer',
  chamberNo: 'Kammernummer',
  buildingAuthority: 'Bauvorlageberechtigung (ja/nein)',
  taxNo: 'Steuernummer',
  accountHolder: 'Kontoinhaber',
  iban: 'IBAN',
  birthDate: 'Geburtsdatum',
  contact: 'Ansprechperson / Kontakt',
  note: 'Notiz',
} as const;

/** Fields that never go into a document for the client (template manual, section 7). */
export const PARTY_PRIVATE = ['taxNo', 'accountHolder', 'iban', 'birthDate'] as const;
